/** Stable label identities; array order is for the menu only, never the reply mapping. */
export const scoreLabels = [
  { code: 'POSITIVE', name: '满意', en: 'Satisfied' },
  { code: 'NEGATIVE_LOGISTICS', name: '不满意-物流问题', en: 'Dissatisfied - Delivery' },
  { code: 'NEGATIVE_QUALITY', name: '不满意-产品质量', en: 'Dissatisfied - Product quality' },
  { code: 'NEGATIVE_PACKAGING', name: '不满意-产品包装', en: 'Dissatisfied - Packaging' },
  { code: 'NEGATIVE_SERVICE', name: '不满意-客户服务', en: 'Dissatisfied - Customer service' },
  { code: 'NEGATIVE_OTHER', name: '不满意-其他', en: 'Dissatisfied - Other' },
] as const;
export type ScoreLabelCode = (typeof scoreLabels)[number]['code'];
export interface ScoreMapping {
  option: string;
  labelCode: ScoreLabelCode;
}
export interface ScoreConfig {
  configVersion: 2;
  configRevision?: string;
  optionMappings: ScoreMapping[];
}
export const scorePrefix = 'RECEPTION_SCORE_';
type Node = { text?: string; type?: string; label?: string; children?: Node[] };
export function scoreCode(label?: string): ScoreLabelCode | undefined {
  return scoreLabels.find(({ code }) => label === scorePrefix + code)?.code;
}
export function collectScoreMappings(nodes: readonly Node[]): ScoreMapping[] {
  const result: ScoreMapping[] = [];
  const visit = (node: Node) => {
    if (node.type === 'concept') {
      const code = scoreCode(node.label);
      if (node.label?.startsWith(scorePrefix) && !code) throw new Error('未知评分标签');
      if (code) {
        if (result.some((item) => item.labelCode === code))
          throw new Error('同一个评分标签只能插入一次');
        result.push({ option: String(result.length + 1), labelCode: code });
      }
    }
    node.children?.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
}
export function upgradeScoreNodes<T extends Node>(nodes: T[]): T[] {
  return nodes.map((node) => ({
    ...node,
    ...(node.type === 'concept' && (node.label === '好评' || node.label === '差评')
      ? { label: scorePrefix + (node.label === '好评' ? 'POSITIVE' : 'NEGATIVE_OTHER') }
      : {}),
    ...(node.children ? { children: upgradeScoreNodes(node.children) } : {}),
  }));
}
/** The final-message editor writes translation results back into content. */
export function normalizeScoreFinalMessage<T extends Node>(context: {
  content: T[];
  translatedContent: T[];
}): { content: T[]; translatedContent: T[] } {
  return {
    content: upgradeScoreNodes(context.content),
    // Historical translations are not edited or sent by this editor.
    translatedContent: [],
  };
}
export function scoreConfigFor(context: {
  content: Node[];
  translatedContent: Node[];
}): ScoreConfig | undefined {
  const mappings = collectScoreMappings(context.content);
  const translated = collectScoreMappings(context.translatedContent);
  if (!mappings.length && !translated.length) return undefined;
  if (!mappings.length) throw new Error('请在邀请原文中插入至少一个评分标签');
  if (
    hasScoreText(context.translatedContent) &&
    JSON.stringify(mappings) !== JSON.stringify(translated)
  ) {
    throw new Error('原文与翻译的评分标签顺序不一致，请调整后保存');
  }
  const hasLegacy = (nodes: Node[]): boolean =>
    nodes.some(
      (node) =>
        (node.type === 'concept' && (node.label === '好评' || node.label === '差评')) ||
        (node.children && hasLegacy(node.children)),
    );
  if (hasLegacy(context.content) || hasLegacy(context.translatedContent))
    throw new Error('请重新编辑邀请，将旧评分变量更新为评分标签');
  return { configVersion: 2, optionMappings: mappings };
}
export function scoreMappingError(context: {
  content: Node[];
  translatedContent: Node[];
}): string | undefined {
  try {
    const config = scoreConfigFor(context);
    if (config) return undefined;
    const labels: string[] = [];
    const visit = (nodes: Node[]) =>
      nodes.forEach((node) => {
        if (node.type === 'concept') labels.push(node.label || '');
        if (node.children) visit(node.children);
      });
    visit(context.content);
    return labels.includes('好评') && labels.includes('差评')
      ? undefined
      : '请插入至少一个评分标签';
  } catch (error) {
    return (error as Error).message;
  }
}
export function scoreNumbers(nodes: Node[]): Record<string, string> {
  // Render duplicate tokens without crashing the editor; validation blocks their save.
  const numbers: Record<string, string> = {};
  let count = 0;
  const visit = (node: Node) => {
    const code = node.type === 'concept' && scoreCode(node.label);
    if (code) numbers[code] = String(++count);
    node.children?.forEach(visit);
  };
  nodes.forEach(visit);
  return numbers;
}
export function rateText(value?: number | null) {
  return value == null ? '—' : `${(value * 100).toFixed(2)}%`;
}

export function hasScoreText(nodes: readonly Node[]): boolean {
  return nodes.some(
    (node) =>
      node.type === 'concept' ||
      ((!node.type || node.type === 'text' || node.type === 'paragraph') &&
        (Boolean(node.text?.trim()) || Boolean(node.children && hasScoreText(node.children)))),
  );
}
export function hasScoreToken(nodes: readonly Node[]): boolean {
  return nodes.some(
    (node) =>
      (node.type === 'concept' && node.label?.startsWith(scorePrefix)) ||
      (node.children && hasScoreToken(node.children)),
  );
}
